import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAppContextController } from "context/AppContext";
import { useMutation } from "react-query";
import { useSnackbar } from "notistack";

import updateInterviewFeedback from "layouts/pages/events/actions/updateInterviewFeedback";

import { Stack, Tooltip, Menu, MenuItem, IconButton } from "@mui/material";
import {
  Info as InfoIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  Description as DescriptionIcon,
  Email as EmailIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";

import MDTypography from "components/MDTypography";
import ApplicantActionModal from "layouts/pages/applicants/components/ApplicantActionModal";
import AddANoteModal from "layouts/pages/applicants/components/AddANoteModal";
import { calculateInterviewOverallFeedback } from "utils/helpers/events";
import GenericMoreButton from "components/GenericMoreButton";
import ApplicantAttachmentsList from "components/ApplicantAttachmentsList";
import InterviewFeedbackButton from "components/InterviewFeedbackButton";
import ApplicantNotesList from "components/ApplicantNotesList";

const ApplicantActions = ({
  id,
  parentComponent,
  row,
  onRemove,
  refetchApplicants,
  customAttachments,
  customTemplates,
  onChangeFeedback,
}) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { currentLoggedUser, currentEvent } = useAppContextController();
  const [isMailModalOpen, setMailModalOpen] = useState(false);
  const [isNotesModalOpen, setNotesModalOpen] = useState(false);
  const [isAttachmentsModalOpen, setAttachmentsModalOpen] = useState(false);
  const [toastAlert, setToastAlert] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const isThumbsUpOpen = Boolean(anchorEl);

  const handleClick = (e) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setAnchorEl(null);
  };

  const updateInterviewFeedbackMutation = useMutation(updateInterviewFeedback, {
    onError: () => enqueueSnackbar("Something went wrong!", { variant: "error" }),
    onSuccess: async (_, data) => {
      enqueueSnackbar("Interview feedback has been updated!", { variant: "success" });
      refetchApplicants();
    },
  });

  const handleUpdateFeedback = async (data) => {
    await updateInterviewFeedbackMutation.mutateAsync({
      applicantId: row?.id || row?._id,
      eventUrl: currentEvent?.eventUrl,
      data,
    });
    // if (onChangeFeedback) onChangeFeedback(data.feedback.rating);
  };

  const handleSaveNote = (noteRec) => {
    const newNote = {
      type: "Interview Note",
      eventUrl: currentEvent?.eventUrl,
      text: noteRec.text,
      firstName: currentLoggedUser?.firstName,
      lastName: currentLoggedUser?.lastName,
      createAgent: currentLoggedUser?._id,
      date: new Date().toISOString(),
      userId: currentLoggedUser?._id,
    };

    return handleUpdateFeedback({ note: newNote });
  };

  const tableActions = [
    {
      label: `${parentComponent} Info`,
      icon: (
        <IconButton
          sx={{ p: 0 }}
          onClick={(e) => {
            e.stopPropagation();
            navigate(
              `/${parentComponent.toLowerCase()}s/${id}/action/${parentComponent.toLowerCase()}info`
            );
          }}
        >
          <InfoIcon fontSize="medium" color="info" />
        </IconButton>
      ),
    },
    {
      label: `Feedback`,
      icon: (
        <>
          <InterviewFeedbackButton
            applicant={row}
            refetchApplicants={refetchApplicants}
            onChangeFeedback={onChangeFeedback}
            isFinal
            eventUrl={currentEvent?.eventUrl}
          />
        </>
      ),
    },
    { isMoreButton: true },
    {
      label: `Remove ${parentComponent}`,
      noTooltip: true,
      icon: (
        <IconButton
          sx={{ p: 0 }}
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
        >
          <CancelIcon color="error" fontSize="medium" />
        </IconButton>
      ),
    },
  ];

  return (
    <>
      <Stack display="flex" direction="row" justifyContent="space-between" spacing={2}>
        {tableActions.map((action) =>
          action.isMoreButton ? (
            <GenericMoreButton
              sx={{ paddingTop: 0, paddingBottom: 0 }}
              fontSize="medium"
              buttons={[
                // {
                //   color: "#9c27b0",
                //   useCustomColor: true,
                //   icon: "email",
                //   tooltip: "Send Message",
                //   clickAction: (e) => {
                //     e.stopPropagation();
                //     setMailModalOpen(true);
                //   },
                // },
                {
                  color: "info",
                  icon: "description",
                  tooltip: `${parentComponent} Notes`,
                  clickAction: (e) => {
                    e.stopPropagation();
                    setNotesModalOpen(true);
                  },
                },
                {
                  color: "dark",
                  icon: "attach_file",
                  tooltip: `${parentComponent} Attachments`,
                  clickAction: (e) => {
                    e.stopPropagation();
                    setAttachmentsModalOpen(true);
                  },
                },
              ]}
            />
          ) : (
            <>
              {action.noTooltip ? (
                action.icon
              ) : (
                <Tooltip title={action.label} placement="top">
                  {action.icon}
                </Tooltip>
              )}
            </>
          )
        )}
      </Stack>

      {/* This button prevents onClick events in modals from propagating to parents, triggering the row's onClick */}
      <button
        type="button"
        onClick={(e) => {
          e?.preventDefault();
          e?.stopPropagation();
        }}
        style={{ height: 0, width: 0 }}
      >
        <ApplicantActionModal
          open={isMailModalOpen}
          setOpen={setMailModalOpen}
          modalInfo={{ data: row, type: `${parentComponent.toLowerCase()}mail` }}
          setToastAlert={setToastAlert}
          currentApplicant={row}
          eventId={currentEvent?._id}
          messageContext="Event"
          attachmentContext="Event"
          customAttachments={customAttachments}
          customTemplates={customTemplates}
        />
        {isNotesModalOpen && (
          <ApplicantNotesList
            currentApplicant={row}
            setCurrentApplicant={() => {}}
            open={isNotesModalOpen}
            setOpen={setNotesModalOpen}
            onSuccess={() => {}}
            isCreateTaskAllowed={false}
            saveNote={handleSaveNote}
            setTaskModalOpen={() => {}}
            showType={false}
          />
        )}
        {isAttachmentsModalOpen && (
          <ApplicantAttachmentsList
            currentApplicant={row}
            setCurrentApplicant={() => {}}
            open={isAttachmentsModalOpen}
            setOpen={setAttachmentsModalOpen}
            onSuccess={refetchApplicants}
          />
        )}
      </button>
    </>
  );
};

export default ApplicantActions;
